/**
 * ClaudeMark — sparkle do Claude/Anthropic, em SVG.
 * Reprodução fiel do ícone oficial: 11 raios irregulares, espessura variável,
 * saindo de um núcleo orgânico. Usado como ornamento editorial.
 *
 * Uso:
 *  <ClaudeMark />                 → glifo (currentColor — herda do pai)
 *  <ClaudeMark variant="badge" /> → selo cheio (quadrado terracota arredondado)
 */
type Variant = "glyph" | "badge";

// 11 raios irregulares saindo do centro (50,50) num viewBox 100x100.
// Cada raio é um triângulo fino com largura/comprimento ligeiramente diferentes
// para reproduzir a textura "desenhada à mão" do sparkle oficial.
const RAYS_PATH =
  "M50 50 L51.5 4 L48.5 4 Z " + // norte
  "M50 50 L78 9 L75 11 Z " +
  "M50 50 L94 28 L92 31 Z " +
  "M50 50 L98 52 L98 48 Z " + // leste
  "M50 50 L92 78 L94 75 Z " +
  "M50 50 L72 96 L69 94 Z " +
  "M50 50 L51 98 L49 98 Z " + // sul
  "M50 50 L28 96 L31 94 Z " +
  "M50 50 L6 80 L8 78 Z " +
  "M50 50 L2 50 L2 47 Z " + // oeste
  "M50 50 L8 22 L11 25 Z";

function Rays() {
  return (
    <>
      <path d={RAYS_PATH} />
      {/* núcleo orgânico */}
      <circle cx="50" cy="50" r="7" />
    </>
  );
}

export function ClaudeMark({
  className = "h-5 w-5",
  variant = "glyph",
  title = "Claude by Anthropic",
}: {
  className?: string;
  variant?: Variant;
  title?: string;
}) {
  if (variant === "badge") {
    return (
      <svg
        viewBox="0 0 100 100"
        role="img"
        aria-label={title}
        className={className}
      >
        <title>{title}</title>
        <rect width="100" height="100" rx="22" fill="oklch(0.68 0.13 40)" />
        <g fill="oklch(0.96 0.02 70)" transform="translate(10 10) scale(0.8)">
          <Rays />
        </g>
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 100 100"
      role="img"
      aria-label={title}
      className={className}
      fill="currentColor"
    >
      <title>{title}</title>
      <Rays />
    </svg>
  );
}

/**
 * Selo editorial "Claude · Anthropic" — referência discreta de "powered by".
 */
export function ClaudeBadge({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-gold/80 ${className}`}
    >
      <ClaudeMark className="h-3.5 w-3.5" />
      <span style={{ fontFamily: "var(--font-display)", letterSpacing: "0.18em" }}>
        Claude · Anthropic
      </span>
    </span>
  );
}
